'use client';

import { ItemImage } from '@/api';
import { isTouchDevice } from '@/helpers/TouchDevice';
import { useHasWindow } from '@/hooks/useHasWindow';
import { useInterval } from '@/hooks/useInterval';
import { ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import React, { CSSProperties, ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSnapCarousel } from 'react-snap-carousel';

const styles = require('./product-page-carousel.module.css');

export interface ProductPageGalleryProps<T> {
    readonly axis?: 'x' | 'y';
    readonly items: T[];
    readonly renderItem: (
        props: ProductPageGalleryRenderItemProps<T>
    ) => React.ReactElement<ProductPageGalleryItemProps>;
    readonly scrollMargin?: boolean;
    readonly activeNavColor?: string;
    readonly isLoading?: boolean;
    readonly className?: string;
    readonly enableFading?: boolean
    readonly enableAutoScroll?: boolean
}

export interface ProductPageGalleryRenderItemProps<T> {
    readonly item: T;
    readonly index: number;
    readonly isSnapPoint: boolean;
}

export interface ProductPageGalleryRef {
    readonly refresh: () => void;
}

// eslint-disable-next-line max-len
const ProductPageGalleryRender = <T extends ItemImage>({ axis, items, renderItem, scrollMargin = false, activeNavColor, isLoading, className, enableFading, enableAutoScroll }: ProductPageGalleryProps<T>, ref: ForwardedRef<ProductPageGalleryRef>) => {
    const {
        scrollRef,
        next,
        prev,
        goTo,
        pages,
        activePageIndex,
        snapPointIndexes,
        refresh,
    } = useSnapCarousel({ axis });

    const scrollContainerRef = useRef<HTMLUListElement>(null);
    useImperativeHandle(ref, () => ({ refresh }));

    const [showUpButton, setShowUpButton] = useState<boolean>(false);
    const [showDownButton, setShowDownButton] = useState<boolean>(false);
    const [mounted, setMounted] = useState<boolean>(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (mounted) {
            scrollRef(scrollContainerRef.current);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollRef, scrollContainerRef.current, mounted]);

    const resetAutoScroll = useInterval(() => {
        if (activePageIndex === (pages.length - 1)) {
            goTo(0);
        } else {
            next();
        }
    }, !isLoading && enableAutoScroll && pages.length > 1 ? 10 * 1000 : null);

    useEffect(() => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            container.addEventListener('scroll', resetAutoScroll);
            return () => { container.removeEventListener('scroll', resetAutoScroll); };
        }
    }, [resetAutoScroll]);

    const thumnailsContainerRef = useRef<HTMLDivElement>(null);
    const center = pages.length === 1;

    useEffect(() => {
        if (thumnailsContainerRef?.current) {
            setShowUpButton(thumnailsContainerRef?.current?.scrollTop >= 106);
            setShowDownButton(thumnailsContainerRef?.current?.scrollHeight - thumnailsContainerRef?.current?.scrollTop !== thumnailsContainerRef?.current?.clientHeight);
        }
    }, [thumnailsContainerRef, setShowUpButton, setShowDownButton]);

    return (
        <div className="flex flex-row space-x-2 noSelect w-full">
            <div
                className="w-[96px] hidden md:block h-[732px] overflow-hidden relative"
                ref={thumnailsContainerRef}
                onScroll={() => {
                    if (thumnailsContainerRef?.current) {
                        setShowUpButton(thumnailsContainerRef?.current?.scrollTop >= 106);
                        setShowDownButton(thumnailsContainerRef?.current?.scrollHeight - thumnailsContainerRef?.current?.scrollTop !== thumnailsContainerRef?.current?.clientHeight);
                    }
                }}>
                <AnimatePresence>
                    {showUpButton &&
                        <motion.div
                            initial={{ opacity: 0, y: -55 }}
                            exit={{ opacity: 0, y: -55 }}
                            animate={{ y: 0, opacity: 1 }}
                            onClick={() => thumnailsContainerRef.current?.scrollBy({ top: -106 * 4, behavior: 'smooth' })}
                            className="absolute top-[10px] w-[96px] h-[40px] flex items-center justify-center  z-20">
                            <ChevronUpIcon className="text-grey3 w-[36px] h-[36px] p-[8px] rounded-full mx-auto bg-white shadow cursor-pointer hover:bg-black hover:text-grey3" />
                        </motion.div>
                    }
                </AnimatePresence>
                <AnimatePresence>
                    {items.length > 7 && showDownButton &&
                        <motion.div
                            initial={{ opacity: 0, y: 55 }}
                            exit={{ opacity: 0, y: 55 }}
                            animate={{ y: 0, opacity: 1 }}
                            onClick={() => thumnailsContainerRef.current?.scrollBy({ top: 106 * 4, behavior: 'smooth' })}
                            className="absolute top-[680px] w-[96px] h-[40px] flex items-center justify-center  z-20 left-0">
                            <ChevronDownIcon className="text-grey3 w-[36px] h-[36px] p-[8px] rounded-full mx-auto bg-white shadow cursor-pointer hover:bg-black hover:text-white" />
                        </motion.div>
                    }
                </AnimatePresence>

                {isLoading ?
                    <>
                        <Skeleton className="h-[96px] w-[96px] mb-[6px]" />
                        <Skeleton className="h-[96px] w-[96px] mb-[6px]" />
                        <Skeleton className="h-[96px] w-[96px] mb-[6px]" />
                        <Skeleton className="h-[96px] w-[96px] mb-[6px]" />
                        <Skeleton className="h-[96px] w-[96px] mb-[6px]" />
                        <Skeleton className="h-[96px] w-[96px] mb-[6px]" />
                        <Skeleton className="h-[96px] w-[96px] mb-[6px]" />

                    </>
                    : items.map((item, index) => {
                        const active = activePageIndex === index;
                        return (
                            <div key={`PPImageIndex${item.id}`} className={clsx('h-[96px] mb-[10px] w-[96px] rounded-[10px] overflow-hidden transition-all', active ? 'opacity-[1] border-[1px] border-[#ccc]' : 'opacity-[0.2]', index === items.length - 1 && '!mb-0')}>
                                {item.type === ItemImage.TypeEnum.Image &&
                                    <motion.img
                                        onClick={() => goTo(index)}
                                        onMouseEnter={() => isTouchDevice() ? false : goTo(index)}
                                        alt=""
                                        className={clsx('cursor-pointer h-[96px] w-[96px] block')}
                                        src={item.url} />
                                }
                                {item.type === ItemImage.TypeEnum.Video &&
                                    <motion.video
                                        onClick={() => goTo(index)}
                                        onMouseEnter={() => isTouchDevice() ? false : goTo(index)}
                                        preload="auto"
                                        playsInline
                                        autoPlay
                                        controls={false}
                                        loop
                                        muted
                                        className={clsx('cursor-pointer h-[96px] w-[96px] block')}
                                        src={item.url} />
                                }

                            </div>
                        );
                    })}
            </div>
            <div
                className={clsx(styles.root, className, 'max-w-[734px] w-full overflow-hidden relative', {
                    [styles.x]: axis === 'x',
                    [styles.y]: axis === 'y',
                    [styles.scrollMargin]: scrollMargin,
                    'after:opacity-0 after:transition-opacity after:absolute after:h-[calc(100%-calc(32px))] after:right-0 after:top-0 after:w-2 after:bg-gradient-to-r from-transparent to-grey11': enableFading,
                    'after:!opacity-100': activePageIndex !== (pages.length - 1),
                    'flex justify-center': center
                })}
            >

                {isLoading ? (<Skeleton className="w-full h-[500px] md:h-[100%] mt-[22px] md:mt-[0]" />) : (
                    <ul className={clsx(styles.scroll, 'max-w-inner flex flex-row rounded-[10px] ')} ref={scrollContainerRef}>
                        {items.map((item, index) =>
                            renderItem({
                                item,
                                index,
                                isSnapPoint: snapPointIndexes.has(index),
                            })
                        )}
                    </ul>
                )}

                {!isLoading &&
                    <>
                        <motion.button
                            initial={{ x: -55 }}
                            animate={{ x: activePageIndex > 0 ? 0 : -55 }}
                            type="button"
                            className="absolute left-[1rem] top-0 bottom-0 m-auto hidden md:block bg-white w-[36px] h-[36px] rounded-[18px] shadow hover:bg-black group z-10"
                            onClick={() => {
                                prev();
                                if (thumnailsContainerRef.current && activePageIndex > 0) {
                                    const activeElement = thumnailsContainerRef.current.childNodes[activePageIndex + 1];
                                    thumnailsContainerRef.current?.scrollTo({ top: (activeElement as HTMLElement)?.offsetTop ? (activeElement as HTMLElement).offsetTop : 0, behavior: 'smooth' });
                                }
                            }}
                        >
                            <ChevronLeftIcon className="text-grey3 group-hover:text-white w-[20px] h-[26px] mx-auto transition-all" />
                        </motion.button>

                        <motion.button
                            animate={{ x: activePageIndex !== (pages.length - 1) ? 0 : 55 }}
                            type="button"
                            className="absolute right-[1rem] top-0 bottom-0 m-auto hidden md:block bg-white w-[36px] h-[36px] rounded-[18px] shadow hover:bg-black group z-10"
                            onClick={() => {
                                next();
                                if (thumnailsContainerRef.current && activePageIndex < pages.length) {
                                    const activeElement = thumnailsContainerRef.current.childNodes[activePageIndex + 1];

                                    thumnailsContainerRef.current?.scrollTo({ top: (activeElement as HTMLElement)?.offsetTop ? (activeElement as HTMLElement).offsetTop : 0, behavior: 'smooth' });
                                }
                            }}
                        >
                            <ChevronRightIcon className="text-grey3 w-[20px] h-[26px] mx-auto group-hover:text-white transition-all" />
                        </motion.button>
                    </>
                }

                <div className="md:hidden">
                    <div className={clsx(styles.controls, { 'invisible 1': pages.length <= 1, '!hidden 1': center })}>
                        <ol className={styles.pagination}>
                            {pages.map((_, i) => (
                                <li
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`CarItem-${i}`}
                                    className={clsx(styles.paginationItem, {
                                        [styles.paginationItemActive]:
                                            i === activePageIndex,
                                    })}
                                >
                                    <motion.button
                                        animate={{
                                            backgroundColor:
                                                i === activePageIndex
                                                    ? activeNavColor || '#0083C9'
                                                    : '#D9D9D9',
                                        }}
                                        type="button"
                                        className={clsx(styles.paginationButton, 'transition-none bg-[#D9D9D9]')}
                                        onClick={() => goTo(i)}
                                    />
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>

            </div>
        </div>
    );
};
export const ProductPageGallery = forwardRef(ProductPageGalleryRender) as <T extends { id: string; url: string }>(props: ProductPageGalleryProps<T> & { ref?: ForwardedRef<ProductPageGalleryRef> }) => React.ReactElement;

export interface ProductPageGalleryItemProps {
    readonly isSnapPoint: boolean;
    readonly width?: number;
    readonly fullWidth?: boolean;
    readonly className?: string;
    readonly children?: React.ReactNode;
    readonly onClick?: () => void;
}

export const ProductPageGalleryItem = ({
    isSnapPoint,
    className,
    fullWidth,
    children,
    onClick,
}: ProductPageGalleryItemProps) => {
    const style: CSSProperties = {
        scrollSnapAlign: isSnapPoint ? 'start' : '',
    };

    if (fullWidth) {
        style.flex = '0 0 auto';
        style.width = '100%';
        style.display = 'flex';
        style.maxHeight = '732px';
        style.overflow = 'hidden';
    }
    return <li className={clsx('cursor-pointer relative', className)} onClick={onClick && onClick} style={style}>{children}</li>;
};
